import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Lock, Wallet, History, Ticket, Infinity as InfinityIcon, Package, Shield } from 'lucide-react';
import { authApi, profileApi, shopApi, topupApi } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { TopupDialog } from '@/components/TopupDialog';
import { formatDateTime, formatRub } from '@/lib/utils';
import { ORDER_STATUS, PROVIDER, TX_STATUS, ruLabel } from '@/lib/labels';

const NICK_RE = /^[a-zA-Z0-9_]{3,16}$/;

// Форматирует «осталось N дней / часов / минут» до истечения.
function formatRemaining(ms: number | null): string {
  if (ms == null) return '';
  if (ms <= 0) return 'истекла';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'меньше минуты';
  const min = Math.floor(sec / 60);
  if (min < 60) return `осталось ${min} мин.`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `осталось ${hr} ч. ${min % 60} мин.`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `осталось ${days} дн. ${hr % 24} ч.`;
  const months = Math.floor(days / 30);
  return `осталось ${months} мес. ${days % 30} дн.`;
}
const MIN_TOPUP_RUB = 1;

export function ProfilePage() {
  const queryClient = useQueryClient();
  const { data: me, isLoading } = useQuery({ queryKey: ['me'], queryFn: authApi.me, retry: false });
  const { data: orders } = useQuery({
    queryKey: ['orders'],
    queryFn: shopApi.myOrders,
    enabled: Boolean(me),
  });
  const { data: txs } = useQuery({
    queryKey: ['transactions'],
    queryFn: profileApi.transactions,
    enabled: Boolean(me),
  });
  const { data: passes } = useQuery({
    queryKey: ['passes'],
    queryFn: profileApi.passes,
    enabled: Boolean(me),
    refetchInterval: 30_000,
  });
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: profileApi.roles,
    enabled: Boolean(me),
    refetchInterval: 30_000,
  });
  const { data: inventory } = useQuery({
    queryKey: ['inventory'],
    queryFn: profileApi.inventory,
    enabled: Boolean(me),
    refetchInterval: 30_000,
  });

  const [nick, setNick] = useState('');
  const [topupAmount, setTopupAmount] = useState(300);
  const [topupOpen, setTopupOpen] = useState(false);

  const bindNick = useMutation({
    mutationFn: profileApi.bindNickname,
    onSuccess: () => {
      toast.success('Никнейм закреплён за вашим Discord');
      queryClient.invalidateQueries({ queryKey: ['me'] });
      setNick('');
    },
    onError: (err: AxiosError<{ error?: string }>) => {
      const code = err.response?.data?.error;
      const map: Record<string, string> = {
        nickname_taken: 'Этот ник уже занят',
        nickname_locked: 'Никнейм уже привязан и изменить его нельзя',
        invalid_nickname: 'Неверный формат ника (a-Z, 0-9, _; 3..16 символов)',
      };
      toast.error(map[code ?? ''] ?? 'Не удалось привязать ник');
    },
  });

  const recheckPendingTopups = useMutation({
    mutationFn: topupApi.recheckPending,
    onSuccess: (r) => {
      if (r.creditedMinor > 0) {
        toast.success(`Зачислено: ${formatRub(r.creditedMinor)}`);
        queryClient.invalidateQueries({ queryKey: ['me'] });
      }
      if (r.creditedMinor > 0 || r.removedExpiredCount > 0) {
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
      }
    },
  });

  useEffect(() => {
    if (!me) return;
    recheckPendingTopups.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);


  if (isLoading) return <div className="p-12 text-eol-desc">Загрузка…</div>;
  if (!me) {
    return (
      <div className="mx-auto max-w-md p-12 text-center">
        <h1 className="text-2xl font-extrabold text-eol-white">Профиль</h1>
        <p className="mt-3 text-eol-desc">Войдите через Discord, чтобы видеть профиль.</p>
        <Button asChild className="mt-6">
          <a href={authApi.loginUrl()}>Войти через Discord</a>
        </Button>
      </div>
    );
  }

  const nickValid = NICK_RE.test(nick);

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-12 py-12 space-y-8">
      {/* User card */}
      <Card>
        <CardContent className="pt-6 flex items-center gap-4 flex-wrap">
          {me.avatar ? (
            <img src={me.avatar} alt="" className="h-16 w-16 rounded-full" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-eol-pink/30" />
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-extrabold text-eol-white">{me.globalName ?? me.username}</h1>
            <p className="text-sm text-eol-desc">ID Discord: {me.discordId}</p>
            <div className="mt-1 flex gap-2 flex-wrap">
              {me.role === 'admin' && <Badge>Админ</Badge>}
              {me.minecraftNickname ? (
                <Badge variant="success" className="gap-1">
                  <Lock className="h-3 w-3" />
                  {me.minecraftNickname}
                </Badge>
              ) : (
                <Badge variant="danger">Ник не привязан</Badge>
              )}
            </div>
          </div>
          <Button variant="secondary" onClick={() => authApi.logout().then(() => location.reload())}>
            Выйти
          </Button>
        </CardContent>
      </Card>

      {/* Nickname bind */}
      {!me.minecraftNickname && (
        <Card>
          <CardHeader>
            <CardTitle>Привязка Minecraft-никнейма</CardTitle>
            <CardDescription>
              Никнейм закрепится за вашим Discord-аккаунтом{' '}
              <strong className="text-eol-pink">навсегда</strong>. Изменить его потом нельзя.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 flex-wrap">
              <Input
                value={nick}
                onChange={(e) => setNick(e.target.value)}
                placeholder="Например: Steve_42"
                className="max-w-xs"
              />
              <Button onClick={() => bindNick.mutate(nick)} disabled={!nickValid || bindNick.isPending}>
                Привязать ник
              </Button>
            </div>
            {nick && !nickValid && (
              <p className="mt-2 text-xs text-eol-red">
                Допустимы латинские буквы, цифры и _. Длина 3..16.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Passes */}
      {me.minecraftNickname && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-eol-pink" /> Мои проходки
            </CardTitle>
            <CardDescription>
              Активный доступы к серверу.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!passes || passes.length === 0 ? (
              <p className="text-eol-desc">
                У вас пока нет активных проходок. Купите проходку в{' '}
                <a href="/shop" className="text-eol-pink underline">магазине</a>.
              </p>
            ) : (
              <ul className="space-y-3">
                {passes.map((p) => {
                  const isInfinite = p.expiresAt == null;
                  const ms = p.expiresAt ? new Date(p.expiresAt).getTime() - Date.now() : null;
                  return (
                    <li
                      key={p.id}
                      className="rounded-lg border border-white/5 bg-eol-black/40 p-3 flex items-center justify-between gap-3 flex-wrap"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-eol-white truncate">
                          {p.serverName ?? 'Все серверы'}
                        </p>
                        <p className="text-xs text-eol-desc tabular-nums">
                          Ник: <span className="text-eol-white">{p.nicknameSnapshot}</span>
                          {' · выдана '}
                          {formatDateTime(p.createdAt)}
                        </p>
                        {p.note && <p className="text-xs text-eol-desc mt-1 italic">{p.note}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {isInfinite ? (
                          <Badge variant="success" className="gap-1">
                            <InfinityIcon className="h-3 w-3" />
                            Бессрочная
                          </Badge>
                        ) : (
                          <>
                            <Badge variant="success">Активна</Badge>
                            <p className="text-xs text-eol-desc tabular-nums">
                              до {formatDateTime(p.expiresAt!)}
                            </p>
                            <p className="text-xs text-eol-pink font-semibold tabular-nums">
                              {formatRemaining(ms)}
                            </p>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Roles */}
      {me.minecraftNickname && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-eol-pink" /> Мои роли
            </CardTitle>
            <CardDescription>
              Активные роли, купленные в магазине.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!roles || roles.length === 0 ? (
              <p className="text-eol-desc">
                У вас пока нет активных ролей. Купить роль можно в{' '}
                <a href="/shop" className="text-eol-pink underline">магазине</a>.
              </p>
            ) : (
              <ul className="space-y-3">
                {roles.map((role) => {
                  const isInfinite = role.expiresAt == null;
                  const ms = role.expiresAt ? new Date(role.expiresAt).getTime() - Date.now() : null;
                  const statusVariant =
                    role.orderStatus === 'delivered'
                      ? 'success'
                      : role.orderStatus === 'failed'
                        ? 'danger'
                        : 'secondary';

                  return (
                    <li
                      key={role.orderId}
                      className="rounded-lg border border-white/5 bg-eol-black/40 p-3 flex items-center justify-between gap-3 flex-wrap"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-eol-white truncate">{role.productName}</p>
                        <p className="text-xs text-eol-desc tabular-nums">
                          {role.serverName ? `Сервер: ${role.serverName} · ` : ''}
                          выдана {formatDateTime(role.createdAt)}
                        </p>
                        {role.paperGroup && (
                          <p className="text-xs text-eol-desc mt-1">
                            Группа: <span className="font-mono text-eol-white">{role.paperGroup}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Badge variant={statusVariant}>{ruLabel(ORDER_STATUS, role.orderStatus)}</Badge>
                        {isInfinite ? (
                          <Badge variant="success" className="gap-1">
                            <InfinityIcon className="h-3 w-3" />
                            Бессрочная
                          </Badge>
                        ) : (
                          <>
                            <p className="text-xs text-eol-desc tabular-nums">
                              до {formatDateTime(role.expiresAt)}
                            </p>
                            <p className="text-xs text-eol-pink font-semibold tabular-nums">
                              {formatRemaining(ms)}
                            </p>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Inventory cart */}
      {me.minecraftNickname && inventory && inventory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-eol-pink" /> Корзина предметов
            </CardTitle>
            <CardDescription>
              Купленные предметы ждут вас в игре. Зайдите на сервер и используйте команду{' '}
              <code className="text-eol-pink">/корзина</code>, чтобы забрать.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {inventory.map((g) => (
                <li
                  key={g.id}
                  className="rounded-lg border border-white/5 bg-eol-black/40 p-3 flex items-center justify-between gap-3 flex-wrap"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-eol-white truncate">
                      {g.displayName ?? g.material}{' '}
                      <span className="text-eol-pink">×{g.count}</span>
                    </p>
                    <p className="text-xs text-eol-desc tabular-nums">
                      Сервер: {g.serverName ?? '—'} · добавлено {formatDateTime(g.createdAt)}
                    </p>
                  </div>
                  <Badge variant={g.status === 'delivered' ? 'success' : g.status === 'cancelled' ? 'danger' : 'secondary'}>
                    {g.status === 'pending' ? 'Ждёт выдачи' : g.status === 'delivered' ? 'Выдано' : 'Отменено'}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Balance + Top-up */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-eol-pink" /> Баланс
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-4xl font-extrabold text-eol-pink">{formatRub(me.balance)}</p>

          <div className="mt-6 space-y-3">
            <p className="text-sm font-semibold text-eol-white">Пополнить:</p>
            <div className="flex gap-2 flex-wrap">
              {[100, 300, 500, 1000, 2000].map((v) => (
                <button
                  key={v}
                  onClick={() => setTopupAmount(v)}
                  className={`px-4 py-2 rounded-md text-sm font-semibold border transition ${
                    topupAmount === v
                      ? 'border-eol-pink/60 bg-eol-pink/15 text-eol-pink'
                      : 'border-white/10 text-eol-desc hover:text-eol-white'
                  }`}
                >
                  {v} ₽
                </button>
              ))}
              <NumberInput
                value={topupAmount}
                onChange={setTopupAmount}
                className="w-36"
                min={MIN_TOPUP_RUB}
                max={50_000}
                step={1}
              />
            </div>

            <div className="mt-4">
              <Button
                onClick={() => setTopupOpen(true)}
                disabled={topupAmount < MIN_TOPUP_RUB}
                size="lg"
                className="gap-2"
              >
                Пополнить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-eol-pink" /> Мои покупки
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orders && orders.length > 0 ? (
            <ul className="divide-y divide-white/5">
              {orders.map((o) => (
                <li
                  key={o.id}
                  className="py-3 grid grid-cols-3 items-center gap-2"
                >
                  <div className="min-w-0 justify-self-start">
                    <p className="font-semibold text-eol-white truncate">{o.productName}</p>
                    <p className="text-xs text-eol-desc tabular-nums">
                      {formatDateTime(o.createdAt)}
                      {o.expiresAt && ` · до ${formatDateTime(o.expiresAt)}`}
                    </p>
                  </div>
                  <span className="text-sm text-eol-desc tabular-nums whitespace-nowrap justify-self-center">
                    {formatRub(o.pricePaidMinor)}
                  </span>
                  <div className="justify-self-end">
                    <Badge
                      variant={
                        o.status === 'delivered'
                          ? 'success'
                          : o.status === 'failed'
                            ? 'danger'
                            : 'secondary'
                      }
                    >
                      {ruLabel(ORDER_STATUS, o.status)}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-eol-desc">Покупок ещё нет.</p>
          )}
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>История пополнений</CardTitle>
        </CardHeader>
        <CardContent>
          {Array.isArray(txs) && txs.length > 0 ? (
            <ul className="divide-y divide-white/5">
              {txs.map((t) => (
                <li
                  key={t.id}
                  className="py-3 grid grid-cols-4 items-center gap-2 text-sm"
                >
                  <span className="text-eol-white truncate min-w-0 justify-self-start">
                    {ruLabel(PROVIDER, t.provider)} · {t.currency}
                  </span>
                  <span className="text-eol-desc tabular-nums whitespace-nowrap justify-self-center">
                    {formatDateTime(t.createdAt)}
                  </span>
                  <span className="text-eol-pink font-semibold tabular-nums whitespace-nowrap justify-self-center">
                    {formatRub(t.amountMinor)}
                  </span>
                  <div className="justify-self-end">
                    <Badge variant={t.status === 'paid' ? 'success' : 'secondary'}>
                      {ruLabel(TX_STATUS, t.status)}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-eol-desc">Пополнений ещё нет.</p>
          )}
        </CardContent>
      </Card>

      <TopupDialog
        open={topupOpen}
        amountMinor={topupAmount * 100}
        amountRub={topupAmount}
        onClose={() => setTopupOpen(false)}
      />
    </div>
  );
}
